import { inject, observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { dateFilter } from "react-bootstrap-table2-filter";
import paginationFactory from "react-bootstrap-table2-paginator";
import { UncontrolledTooltip } from "reactstrap";
import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";
import { Invoice, InvoiceLineItem, InvoiceTaxLineItem } from "src/model/Financial";
import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { CustomerStore } from "src/stores/CustomerStore";

import {
    faDownload,
    faExclamationCircle,
    faExclamationTriangle,
    faFile,
    faFileInvoiceDollar,
    faFlag,
    faPenSquare,
    faPrint,
    faSearch,
    faTrashAlt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as fileUtil from "../../common/Methods/FileUtil";
import LoadingWidget from "../common/LoadingWidget";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { errorMessage, successMessage } from "../common/Toast";
import InvoicesDialog from "./InvoiceDialog";
import { HorseForSaleLink } from "../common/HorseForSaleLink";

export interface State {
    showInvoiceDialog: boolean;
    showVoidedInvoices: boolean;
    invoiceSearch: number | null;
    currentEditingInvoice: Invoice | null;
    invoicesSelected: Array<number>;
}
export interface Props {
    customerStore?: CustomerStore;
    attachmentLogEndpoints?: AttachmentLogEndpoints;
    customerEndpoints?: CustomerEndpoints;
    configurationStore?: ConfigurationStore;
}

@inject("attachmentLogEndpoints")
@inject("customerEndpoints")
@inject("customerStore")
@observer
export default class InvoicesComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            invoiceSearch: null,
            showVoidedInvoices: false,
            showInvoiceDialog: false,
            currentEditingInvoice: null,
            invoicesSelected: []
        };
    }

    closeInvoiceDialog = () => {
        this.setState({ showInvoiceDialog: false, currentEditingInvoice: null });
    };

    voidInvoice = async (invoiceId: number) => {
        try {
            const goAhead = await SimpleConfirmation(`Are you sure you'd like to void Invoice #${invoiceId}?`);
            if (!goAhead) {
                return;
            }

            await this.props.customerStore!.voidInvoice(invoiceId);
            successMessage(`Invoice #${invoiceId} has been voided.`);
            this.setState({ currentEditingInvoice: null });
        } catch (error) {
            errorMessage(error);
        }
    };

    downloadInvoices = async (invoiceIds: Array<number>) => {
        try {
            const blob = await this.props.customerEndpoints!.downloadInvoice(invoiceIds);
            invoiceIds.length > 1
                ? fileUtil.DownloadBlob(`Invoices_${invoiceIds.join("_")}.pdf`, blob)
                : fileUtil.DownloadBlob(`Invoice_${invoiceIds[0]}.pdf`, blob);
        } catch (error) {
            errorMessage(error);
        }
    };

    filterByShowOnlyNotVoidInvoices = (invoices: Invoice[]) => {
        return invoices.filter((i: Invoice) => i.dateVoided === null);
    };

    handleOnSelect = (invoice: Invoice, isSelect: boolean) => {
        if (isSelect) {
            this.setState(() => ({
                invoicesSelected: [...this.state.invoicesSelected, invoice.id]
            }));
        } else {
            this.setState(() => ({
                invoicesSelected: this.state.invoicesSelected.filter(x => x !== invoice.id)
            }));
        }
    };

    handleOnSelectAll = (isSelect: boolean, rows: any[]) => {
        const ids = rows.map(row => row.id);
        if (isSelect) {
            this.setState(() => ({
                invoicesSelected: ids
            }));
        } else {
            this.setState(() => ({ invoicesSelected: [] }));
        }
    };

    // TODO: If this logic is repeated elsewhere, then move it to a model function
    calculateInvoiceStatus = (row: Invoice): string => {
        if (row.dateVoided !== null) {
            return "Voided";
        } else if (row.settlementItems.length > 0) {
            if (row.balance === 0) {
                return "Paid";
            } else {
                return "Partially Paid";
            }
        } else {
            return "Outstanding";
        }
    };

    render() {
        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            return null;
        }

        const account = customer.account;

        const data = account.account.invoices
            .filter(
                invoice =>
                    this.state.invoiceSearch === null ||
                    invoice.id === this.state.invoiceSearch ||
                    (invoice.horseForSaleTag && invoice.horseForSaleTag.hipNumber === this.state.invoiceSearch)
            )
            .filter(invoice => this.state.showVoidedInvoices || invoice.dateVoided === null);

        const columns = [
            {
                dataField: "issueDate",
                text: "Invoice Date",
                formatter: (cellContent: any, row: Invoice) => (row.issueDate ? moment(row.issueDate).format("YYYY/MM/DD") : " - "),
                sort: true,
                sortFunc: (a: any, b: any, order: "asc" | "desc") => {
                    return order === "asc" ? a - b : b - a;
                }
            },
            {
                dataField: "invoiceNumber",
                text: "Invoice #",
                formatter: (cellContent: any, row: Invoice) => row.id
            },
            {
                dataField: "horseForSaleId",
                text: "Horse for Sale",
                formatter: (cellContent: any, row: Invoice) => {
                    if (row.horseForSaleTag) {
                        return <HorseForSaleLink horseForSale={row.horseForSaleTag} targetBlank={true} />;
                    } else {
                        return "";
                    }
                },
                sort: true,
                sortFunc: (_: any, __: any, order: "asc" | "desc", field: string, a: Invoice, b: Invoice) => {
                    const hfsA = a.horseForSaleTag;
                    const hfsB = b.horseForSaleTag;
                    const aPrime = hfsA ? hfsA.sale.id * 1000000 + (hfsA.hipNumber || hfsA.id) : 0;
                    const bPrime = hfsB ? hfsB.sale.id * 1000000 + (hfsB.hipNumber || hfsB.id) : 0;
                    return order === "asc" ? aPrime - bPrime : bPrime - aPrime;
                }
            },
            {
                dataField: "description",
                text: "Description",
                formatter: (cellContent: any, row: Invoice) => (row.description ? row.description : "-")
            },
            {
                dataField: "subtotal",
                text: "Subtotal",
                formatter: (cellContent: any, row: Invoice) => {
                    const subtotal = row.lineItems.reduce(
                        (amount, item: InvoiceLineItem) => amount + (item.entryType.debitCredit === "CREDIT" ? -1 : 1) * item.amountInCents,
                        0
                    );
                    return "$" + fileUtil.CovertAndFormatCentsToDollars(subtotal);
                }
            },
            {
                dataField: "tax",
                text: "Tax",
                formatter: (cellContent: any, row: Invoice) => {
                    const tax = row.invoiceTaxes.reduce((amount, item: InvoiceTaxLineItem) => amount + item.amountInCents, 0);
                    return "$" + fileUtil.CovertAndFormatCentsToDollars(tax);
                }
            },
            {
                dataField: "total",
                text: "Total Amount",
                formatter: (cellContent: any, row: Invoice) => {
                    return "$" + fileUtil.CovertAndFormatCentsToDollars(cellContent);
                }
            },
            {
                dataField: "balance",
                text: "Invoice Balance",
                formatter: (cellContent: any, row: Invoice) => {
                    return "$" + fileUtil.CovertAndFormatCentsToDollars(cellContent);
                }
            },
            {
                dataField: "date ",
                text: "Due",
                formatter: (cellContent: any, row: Invoice) => (row.dueDate ? moment(row.dueDate).format("YYYY-MM-DD") : " - ")
            },
            {
                dataField: "status",
                text: "Status",
                formatter: (cellContent: any, row: Invoice) => this.calculateInvoiceStatus(row)
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "110px" },
                formatter: (cellContent: any, row: Invoice) => {
                    const invoiceStatus = this.calculateInvoiceStatus(row);
                    return (
                        <div style={{ width: "100px" }}>
                            <div className="btn-group btn-group-toggle mr-2">
                                <button
                                    onClick={() => {
                                        this.setState({ currentEditingInvoice: row, showInvoiceDialog: true });
                                    }}
                                    id={`tooltipEdit${row.id}`}
                                    className="btn btn-secondary btn-sm"
                                    //disabled={invoiceStatus === "Paid"}
                                >
                                    {invoiceStatus !== "Outstanding" ? <FontAwesomeIcon icon={faFile} /> : <FontAwesomeIcon icon={faPenSquare} />}
                                </button>
                                <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                    {invoiceStatus !== "Outstanding" ? "View Invoice" : "Edit Invoice"}
                                </UncontrolledTooltip>
                                <button
                                    onClick={() => this.voidInvoice(row.id)}
                                    id={`tooltipVoid${row.id}`}
                                    className="btn btn-secondary btn-sm"
                                    disabled={row.settlementItems.length > 0 || row.dateVoided !== null}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                                <UncontrolledTooltip placement="top" target={`tooltipVoid${row.id}`} delay={0} className="tooltipstrap">
                                    Void Invoice
                                </UncontrolledTooltip>
                            </div>
                            <button onClick={() => this.downloadInvoices([row.id])} id={`tooltipDownload${row.id}`} className="btn btn-dark btn-sm">
                                <FontAwesomeIcon icon={faDownload} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDownload${row.id}`} delay={0} className="tooltipstrap">
                                Download Invoice
                            </UncontrolledTooltip>
                        </div>
                    );
                }
            }
        ];

        const selectRow = {
            mode: "checkbox",
            clickToSelect: false,
            selected: this.state.invoicesSelected,
            onSelect: this.handleOnSelect,
            onSelectAll: this.handleOnSelectAll
        };

        return (
            <>
                <div className="card shadow mb-5">
                    <div className="card-header">
                        <div className="row mx-0">
                            <div className="col-12">Invoices</div>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="col-12 px-0">
                            <div className="card shadow-sm mb-4">
                                <div className="card-body py-2">
                                    <div className="row">
                                        <div className="col-7">
                                            <div className="form-group">
                                                <label htmlFor="firstname">Search</label>
                                                <div className="input-group">
                                                    <div className="input-group-prepend">
                                                        <div className="input-group-text">
                                                            <FontAwesomeIcon icon={faSearch} />
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        id="invoiceSearch"
                                                        placeholder="Search by Hip # or Invoice #..."
                                                        name="invoiceSearch"
                                                        value={this.state.invoiceSearch ? this.state.invoiceSearch : ""}
                                                        onChange={e => {
                                                            const invoiceSearch = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                                            this.setState({ invoiceSearch });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-5">
                                            <div className="form-group ml-5 mt-3">
                                                <div className="form-check form-check-inline mr-2 pt-3">
                                                    <label className="form-check-label mt-2" htmlFor="showVoidedInvoices">
                                                        <input
                                                            className="form-check-input mr-3"
                                                            type="checkbox"
                                                            name="showVoidedInvoices"
                                                            id="showVoidedInvoices"
                                                            checked={this.state.showVoidedInvoices}
                                                            onChange={e => this.setState({ showVoidedInvoices: e.target.checked })}
                                                        />
                                                        Show voided invoices
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 px-0 mb-4">
                            <button
                                type="button"
                                className={`btn btn-sm mr-2 ${this.state.invoicesSelected.length > 0 ? `btn-primary` : `btn-secondary`}`}
                                data-toggle="button"
                                aria-pressed="false"
                                disabled={this.state.invoicesSelected.length === 0}
                                onClick={() => this.downloadInvoices(this.state.invoicesSelected)}
                            >
                                <FontAwesomeIcon icon={faPrint} className="mr-2" />
                                Print {this.state.invoicesSelected.length > 0 ? "Invoices" : "Invoice"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                    this.setState({ currentEditingInvoice: null, showInvoiceDialog: true });
                                }}
                            >
                                <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" />
                                Create an Invoice
                            </button>
                        </div>
                        <div className="col-12 px-0 mt-5 mb-3">
                            <span>
                                {account.pastDueBalance > 0 && <FontAwesomeIcon icon={faExclamationCircle} className="mr-2 text-danger" />}
                                <strong>Past Due Amount:</strong>{" "}
                                <span className={account.pastDueBalance > 0 ? "text-danger" : ""}>
                                    ${fileUtil.CovertAndFormatCentsToDollars(account.pastDueBalance)}
                                </span>
                            </span>
                        </div>
                        <div className="table-responsive">
                            <BootstrapTable
                                keyField="id"
                                remote={false}
                                hover={true}
                                bordered={false}
                                // striped={true}
                                columns={columns}
                                bootstrap4={true}
                                classes="table table-sm"
                                data={data.slice().sort((a, b) => b.issueDate - a.issueDate)}
                                // noDataIndication={() => <div><span>No results found.</span></div>}
                                pagination={paginationFactory()}
                                selectRow={selectRow}
                                noDataIndication={() =>
                                    customerStore.loading ? (
                                        <LoadingWidget />
                                    ) : (
                                        <div>
                                            <span>No results found.</span>
                                        </div>
                                    )
                                }
                                rowClasses={(row: Invoice): string => {
                                    let classes = "";
                                    if (
                                        row.dueDate &&
                                        moment(row.dueDate)
                                            .hour(23)
                                            .minute(59)
                                            .isBefore(new Date()) &&
                                        row.balance > 0
                                    ) {
                                        classes += "row-flagged";
                                    }
                                    return classes;
                                }}
                            />
                            {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                    Otherwise the `noDataIndication` wouldn't work */}
                            {customerStore.loading && <div />}
                        </div>
                    </div>
                </div>
                <InvoicesDialog
                    showInvoiceDialog={this.state.showInvoiceDialog}
                    currentEditingInvoice={this.state.currentEditingInvoice}
                    closeInvoiceDialog={this.closeInvoiceDialog}
                />
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/InvoicesComponent.tsx